import * as React from 'react';
import * as styles from './Popup.css';
import { IAIcon } from '../icon/Icon';
import { IFontSlots } from '../../interfaces/IFontSlots';

let instanceCounter = 0;

export interface IIAPopupProps {
  headline?: string;
  headlineColor?: string;
  headlineSize?: number;
  headlineWidth?: string;
  backgroundImage?: string;
  content: JSX.Element;
  closeButtonColor?: string;
  closeButtonBackground?: string;
  contentOverflow?: "visible" | "hidden" | "clip" | "scroll" | "auto";
  styles?: React.CSSProperties;
  zIndex?: number;
  fontSlots?: IFontSlots;
  close?: () => void;
}

export class IAPopup extends React.Component<IIAPopupProps, {}> {
  private dialogRef = React.createRef<HTMLDivElement>();
  private headlineId: string;
  private boundKeyDown = (e: KeyboardEvent) => this.onKeyDown(e);

  constructor(props: IIAPopupProps) {
    super(props);
    this.headlineId = `ia-popup-headline-${++instanceCounter}`;
  }

  public componentDidMount(): void {
    document.addEventListener('keydown', this.boundKeyDown);
    // Move focus into the dialog
    const dialog = this.dialogRef.current;
    if (!dialog) return;
    const focusable = dialog.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length > 0) {
      focusable[0].focus();
    } else {
      dialog.focus();
    }
  }

  public componentWillUnmount(): void {
    document.removeEventListener('keydown', this.boundKeyDown);
  }

  private onKeyDown(e: KeyboardEvent): void {
    const dialog = this.dialogRef.current;
    if (!dialog) return;

    if (e.key === 'Escape' && this.props.close) {
      e.stopImmediatePropagation();
      this.props.close();
      return;
    }

    if (e.key === 'Tab') {
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }

  public render(): JSX.Element {
    const style = this.props.styles ?? {};
    return (
      <div
        className={styles.IA_popupBackground}
        style={{ zIndex: this.props.zIndex }}
        onClick={() => {
          if (this.props.close) {
            this.props.close();
          }
        }}
      >
        <div
          ref={this.dialogRef}
          role="dialog"
          aria-modal={true}
          aria-labelledby={this.props.headline ? this.headlineId : undefined}
          aria-label={!this.props.headline ? "Dialog" : undefined}
          tabIndex={-1}
          className={styles.IA_popupContentBox}
          onClick={(e) => e.stopPropagation()}
          style={{
            ...style,
            backgroundImage: this.props.backgroundImage ? `url(${this.props.backgroundImage})` : "none",
            overflowY: this.props.contentOverflow,
          }}
        >
          {this.props.close != undefined &&
            <button
              className={styles.IA_popupCloseButton}
              aria-label="Close"
              onClick={() => this.props.close()}
            >
              <IAIcon
                title={"Close"}
                size={24}
                color={this.props.closeButtonColor ? this.props.closeButtonColor : "#000000"}
                style={{
                  backgroundColor: this.props.closeButtonBackground,
                  borderRadius: 24,
                  width: 24,
                  height: 24,
                  lineHeight: "24px",
                  padding: this.props.closeButtonBackground ? 2 : 0
                }}
              />
            </button>
          }
          {this.props.headline &&
            <h1
              id={this.headlineId}
              style={{
                color: this.props.headlineColor ? this.props.headlineColor : "#333333",
                fontSize: this.props.headlineSize ? this.props.headlineSize : 19,
                width: this.props.headlineWidth ? this.props.headlineWidth : "100%",
                fontFamily: this.props.fontSlots?.heading?.fontFamily
              }}
            >
              {this.props.headline}
            </h1>
          }
          {this.props.content}
        </div>
      </div>
    );
  }
}